/**
 * @author      Shivani Mehra
 * @date        Oct, 2025
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  Container,
  Row,
  Table,
  Card,
  Form,
  Alert,
} from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from 'react-bs-datatable';
import { useNavigate, Link } from 'react-router-dom';
import Confirm from '../../Confirm';
import jwt_decode from 'jwt-decode';
import helper from '../../common/helper';
import * as constants from '../../../constants/CONSTANT';
import { FaTrash, FaEdit, FaPlus } from 'react-icons/fa';
import { ShimmerTable } from 'react-shimmer-effects';
import WatconnectApi from '../../../api/WatconnectApi';
import DataApi from '../../../api/DataApi';

const Templates = ({ socket, selectedWhatsAppSetting }) => {
  const navigate = useNavigate();
  const [category, setCategory] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [body, setBody] = useState([]);
  const [templateDelete, setTemplateDelete] = useState(false);
  const [rowData, setRowData] = useState();
  const [isSpinner, setIsSpinner] = useState(false);
  const [userInfo, setUserInfo] = useState({});
  const categories = ['AUTHENTICATION', 'UTILITY', 'MARKETING'];
  const [alerts, setAlerts] = useState([]);
  const watconnectApi = new WatconnectApi('webhook_template');
  const dataApi = new DataApi('watconnect');

  useEffect(() => {
    if (!socket) {
      console.log('⚠️ No socket found in Templates component');
      return;
    }
    console.log('✅ Socket available in Templates:', socket.id);

    const showAlert = (type, data) => {
      console.log(`📩 Received ${type} update:`, data);

      const newAlert = { id: Date.now(), type, data };
      setAlerts(prev => [...prev, newAlert]);

      setTimeout(() => {
        setAlerts(prev => prev.filter(a => a.id !== newAlert.id));
      }, 300000);
    };

    const handleStatusUpdate = async data => {
      try {
        showAlert('status', data);

        const {
          message_template_id,
          message_template_name,
          message_template_language,
          status,
        } = data;

        await dataApi.post(`/template`, {
          template_id: message_template_id,
          template_name: message_template_name,
          language: message_template_language,
          status,
          primaryKeys: ['template_name'],
        });

        fetchAllTemplates(selectedWhatsAppSetting);
      } catch (error) {
        console.error('❌ Error handling status update:', error);
      }
    };

    const handleCategoryUpdate = async data => {
      try {
        showAlert('category', data);

        const {
          message_template_id,
          message_template_name,
          message_template_language,
          new_category,
        } = data;

        await dataApi.post(`/template`, {
          template_id: message_template_id,
          template_name: message_template_name,
          language: message_template_language,
          category: new_category,
          primaryKeys: ['template_name'],
        });
      } catch (error) {
        console.error('❌ Error handling category update:', error);
      }
    };

    socket.on('template_status_update', handleStatusUpdate);
    socket.on('template_category_update', handleCategoryUpdate);

    return () => {
      socket.off('template_status_update', handleStatusUpdate);
      socket.off('template_category_update', handleCategoryUpdate);
    };
  }, [socket, selectedWhatsAppSetting]);

  useEffect(() => {
    if (selectedWhatsAppSetting) {
      fetchAllTemplates(selectedWhatsAppSetting);
    } else {
      setIsSpinner(true);
    }
  }, [selectedWhatsAppSetting]);

  const onFilterType = event => {
    const selectedCategory = event.target.value;
    setCategory(selectedCategory);

    let filtered = body;

    if (selectedCategory) {
      filtered = filtered.filter(
        data => data.category.toLowerCase() === selectedCategory.toLowerCase()
      );
    }

    if (searchQuery) {
      filtered = filtered.filter(
        data =>
          data.templatename.toLowerCase().includes(searchQuery.toLowerCase()) ||
          data.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
          data.language.toLowerCase().includes(searchQuery.toLowerCase()) ||
          data.status.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredData(filtered);
  };

  // Add a search query state
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = event => {
    setSearchQuery(event.target.value);
    onFilterType({ target: { value: category } });
  };
  const fetchAllTemplates = async selectedWhatsAppSetting => {
    let user = jwt_decode(localStorage.getItem('watconnect_token'));
    setUserInfo(user);

    const result = await dataApi.fetchById(
      `watconnect_template?business_number=${selectedWhatsAppSetting}`
    );

    if (result.error || result.success === false) {
      toast.error(result?.error?.message);
      setBody([]);
      setFilteredData([]);
      setIsSpinner(true);
      return;
    }

    if (result?.data?.data && result?.data?.data.length > 0) {
      // ✅ Filter out pending templates
      const validTemplates = result.data.data.filter(
        t =>
          t.status &&
          t.status.toUpperCase() !== 'PENDING_DELETION' &&
          t.status.toUpperCase() !== 'DELETED'
      );

      const transformedData = validTemplates.map(row => {
        // Handle carousel templates
        if (row.template_type === 'carousel' && Array.isArray(row.cards)) {
          const cards = row.cards.map(card => {
            const header = card.header || {};
            const body = card.body || {};
            const buttons = card.buttons || [];

            return {
              header_type: header.format || '',
              header_text: header.format === 'TEXT' ? header.text : '',
              header_image_url: header.format === 'IMAGE' ? header.example : '',
              header_document_url:
                header.format === 'DOCUMENT' ? header.example : '',
              header_video_url: header.format === 'VIDEO' ? header.example : '',
              body_text: body.text || '',
              body_text_example: body.example || [],
              buttons: buttons.map(btn => ({
                type: btn.type,
                text: btn.text,
                ...(btn.type === 'PHONE_NUMBER' && {
                  phone_number: btn.phone_number,
                }),
                ...(btn.type === 'URL' && { url: btn.url }),
              })),
            };
          });

          return {
            id: row.id,
            name: row.template_name,
            templateid: row.template_id,
            templatename: row.template_name,
            language: row.language,
            status: row.status || 'PENDING',
            category: row.category,
            template_type: 'carousel',
            message_body: row.message_body || '',
            footer: row.footer || '',
            cards,
            lastmodifieddate: row.lastmodifieddate,
          };
        }

        // Handle normal templates (with file attachments)
        const fileUrl = row?.file_data?.file_url
          ? row.file_data.file_url.replace(/\\/g, '/')
          : null;
        const mimeType = row?.file_data?.mime_type || '';
        let header_image_url = '';
        let header_video_url = '';
        let header_document_url = '';

        if (row.header === 'IMAGE') header_image_url = fileUrl;
        else if (row.header === 'VIDEO') header_video_url = fileUrl;
        else if (row.header === 'DOCUMENT') header_document_url = fileUrl;

        return {
          id: row.id,
          name: row.template_name,
          templateid: row.template_id,
          templatename: row.template_name,
          language: row.language,
          status: row.status || 'PENDING',
          category: row.category,
          header: row.header !== 'null' ? row.header : '',
          header_text: row.header === 'TEXT' ? row.header_body : '',
          header_image_url: row.header === 'IMAGE' ? header_image_url : '',
          header_video_url: row.header === 'VIDEO' ? header_video_url : '',
          header_document_url:
            row.header === 'DOCUMENT' ? header_document_url : '',
          message_body: row.message_body || '',
          footer: row.footer !== 'null' ? row.footer : '',
          buttons: Array.isArray(row.buttons)
            ? row.buttons.map(btn => ({
                type: btn.type,
                text: btn.text,
                ...(btn.type === 'PHONE_NUMBER' && {
                  phone_number: btn.phone_number,
                }),
                ...(btn.type === 'URL' && { url: btn.url }),
              }))
            : [],
          lastmodifieddate: row.lastmodifieddate,
        };
      });

      // ✅ Sort templates by latest modified date
      const sortedData = transformedData.sort(
        (a, b) => new Date(b.lastmodifieddate) - new Date(a.lastmodifieddate)
      );

      setBody(sortedData);
      setFilteredData(sortedData);
    } else {
      setBody([]);
      setFilteredData([]);
    }

    setIsSpinner(true);
  };

  const labels = { beforeSelect: ' ' };
  const header = [
    {
      title: 'Name',
      prop: 'templatename',
      isFilterable: true,
      cell: row => {
        const formatName = name => {
          if (!name) return '';
          return name
            .toLowerCase()
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
        };

        const displayName = formatName(row.templatename);

        return (
          <Link
            to={'/template/view'}
            state={row}
            style={{ textDecoration: 'none' }}
          >
            {displayName}
          </Link>
        );
      },
    },
    { title: 'category', prop: 'category', isFilterable: true },
    { title: 'language', prop: 'language', isFilterable: true },
    {
      title: 'Status',
      prop: 'status',
      isFilterable: true,
      cell: row => {
        return (
          <>
            {row.status === 'APPROVED' ? (
              <Button
                className='btn-sm'
                variant=''
                style={{
                  borderRadius: '7px',
                  backgroundColor: 'rgb(154 235 233)',
                  cursor: 'default',
                }}
              >
                {row.status}
              </Button>
            ) : row.status === 'PENDING' ? (
              <Button
                className='btn-sm'
                variant=''
                style={{
                  borderRadius: '15px',
                  backgroundColor: 'rgb(241 218 177)',
                  cursor: 'default',
                }}
              >
                {row.status}
              </Button>
            ) : row.status === 'REJECTED' ? (
              <Button
                className='btn-sm'
                variant=''
                style={{
                  borderRadius: '15px',
                  backgroundColor: 'rgb(248 195 200)',
                  cursor: 'default',
                }}
              >
                {row.status}
              </Button>
            ) : null}
          </>
        );
      },
    },
    {
      title: 'Created At',
      prop: 'lastmodifieddate',
      isFilterable: false,
      cell: row => {
        const dateToShow = row.lastmodifieddate;
        if (!dateToShow) return '-';

        const formattedDate = new Date(dateToShow).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });

        return (
          <span
            className='text-dark px-2 py-1'
            style={{
              borderRadius: '10px',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            {formattedDate}
          </span>
        );
      },
    },
    {
      title: 'Actions',
      prop: 'id',
      cell: row => (
        <>
          <Button
            className='btn-sm mx-2'
            variant='danger'
            onClick={() => removeSelectedRow({ row })}
            title='Delete'
          >
            <i className='fa-regular fa-trash-can'></i>
          </Button>
          {/* <Button
            className='btn-sm mx-2'
            variant='primary'
            onClick={() => cloneRecord({ row })}
            title='Clone'
          >
            <i className='fa-solid fa-clone'></i>
          </Button> */}
        </>
      ),
    },
  ].filter(Boolean);

  const cloneRecord = data => {
    console.log('data', data);
    navigate(`/template/add`, {
      state: { ...data?.row, type: 'clone' },
    });
  };

  const addNewTemplate = () => {
    navigate(`/template/add`);
  };

  const editTemplate = data => {
    navigate(`/template/add`, { state: data.row });
  };

  const removeSelectedRow = data => {
    setTemplateDelete(true);
    setRowData(data.row);
  };

  const deleteTemplate = async () => {
    if (rowData.templateid && rowData.templatename) {
      const result = await watconnectApi.deleteUrl(
        `/template?hsm_id=${rowData.templateid}&name=${rowData.templatename}&whatsapp_setting_number=${selectedWhatsAppSetting}`
      );
      if (result.success) {
        setTemplateDelete(false);
        toast.success('Template deleted successfully.');
        fetchAllTemplates(selectedWhatsAppSetting);
      } else {
        setTemplateDelete(false);
        toast.error(result?.error?.message || 'Failed to delete template.');
      }
    }
  };

  return (
    <>
      <Container className='mt-5'>
        <Row className='mx-5 mb-4'>
          <Card className='p-4 bg-white shadow-sm border-0 rounded-3'>
            <h5 className='mb-2 fw-bold'>💡 Quick Guide</h5>
            <small>
              WhatsApp templates allow businesses to send structured,
              pre-approved messages such as alerts, updates, and reminders. To
              ensure successful delivery, templates must comply with Meta's
              policies and follow the approved formatting rules.
              <br></br>
              👉 For detailed guidance, check the official{' '}
              <a
                target='_blank'
                href='https://developers.facebook.com/docs/whatsapp/updates-to-pricing/new-template-guidelines/'
                rel='noreferrer'
              >
                Meta Template Guidelines
              </a>
            </small>
          </Card>
        </Row>
        <Row className='mx-5 mb-4'>
          {alerts?.map(alert => (
            <Alert
              key={alert?.id}
              variant={
                alert?.type === 'status'
                  ? alert?.data?.status === 'APPROVED'
                    ? 'success'
                    : 'warning'
                  : 'info'
              }
              dismissible
              onClose={() =>
                setAlerts(prev => prev.filter(a => a?.id !== alert?.id))
              }
            >
              {alert?.type === 'status' ? (
                <>
                  <strong>Template {alert?.data?.status}!</strong> <br />
                  {alert?.data?.message_template_name} (
                  {alert?.data?.message_template_language})
                  {alert?.data?.reason && <> – Reason: {alert?.data?.reason}</>}
                </>
              ) : (
                <>
                  <strong>Category Updated!</strong> <br />
                  {alert?.data?.message_template_name} moved to{' '}
                  <b>{alert?.data?.new_category}</b>
                </>
              )}
            </Alert>
          ))}
        </Row>
        <Row className='mx-5 g-0'>
          <Col lg={12} className='mb-3'>
            <Row className='g-0'>
              <Col lg={12}>
                <div className='bg-white shadow-sm border-0 rounded-3 p-4'>
                  <h5 className='fw-semibold mb-1'>📦 All Templates</h5>

                  {isSpinner ? (
                    <DatatableWrapper
                      body={body}
                      headers={header}
                      paginationOptionsProps={{
                        initialState: {
                          rowsPerPage: 10,
                          options: [5, 10, 15, 20],
                        },
                      }}
                    >
                      <Row className='mb-3'>
                        <Col
                          lg={3}
                          sm={10}
                          xs={10}
                          className='d-flex flex-col justify-content-end align-items-end'
                        >
                          <Filter />
                        </Col>
                        <Col
                          lg={4}
                          sm={2}
                          xs={2}
                          className='d-flex flex-col justify-content-start align-items-start'
                        >
                          <PaginationOptions labels={labels} />
                        </Col>
                        <Col
                          lg={5}
                          sm={12}
                          xs={12}
                          className='mt-2 d-flex flex-col justify-content-end align-items-end'
                        >
                          <Button
                            variant='primary'
                            className='new-list-btn'
                            onClick={addNewTemplate}
                          >
                            <FaPlus /> Add New Template
                          </Button>
                        </Col>
                      </Row>

                      <div className='table-responsive'>
                        <Table
                          striped
                          bordered
                          className='related-list-table'
                          responsive='sm'
                        >
                          <TableHeader />
                          <TableBody />
                        </Table>
                      </div>
                      <Pagination />
                    </DatatableWrapper>
                  ) : (
                    <ShimmerTable row={10} col={4} />
                  )}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
      {templateDelete && (
        <Confirm
          show={templateDelete}
          onHide={() => setTemplateDelete(false)}
          deleteTemplate={deleteTemplate}
          title='Confirm Delete'
          message='You are going to delete the record. Are you sure?'
          table='template'
        />
      )}

      <ToastContainer />
    </>
  );
};

export default Templates;
